import React, { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '@swyftx/aviary/atoms/Button';
import { Input } from '@swyftx/aviary/atoms/Input';
import { FlexLayout } from '@swyftx/aviary/atoms/Layout/Flex';
import { Separator } from '@swyftx/aviary/atoms/Seperator/Seperator';
import { Slider } from '@swyftx/aviary/atoms/Slider/Slider';
import { Body, Numeric } from '@swyftx/aviary/atoms/Typography';
import { useSnackBars } from '@swyftx/aviary/complex/GlobalSnackBars/useSnackBars';
import { ReviewItem } from '@swyftx/aviary/molecules/ReviewItem/ReviewItem';
import StepModal from '@swyftx/aviary/molecules/StepModal';
import { MarginPartialCloseError, UpdateMarginPosition } from '@swyftx/margin/build/types';

import { formatCurrency } from '@shared/utils';

import { SwyftxMarginStalePriceCloseBanner } from '@routes/SwyftxMargin/components/General/SwyftxMarginStalePriceBanner';

import Big from 'big.js';
import { DateTime } from 'luxon';
import { useMarginAccount } from 'src/lib/margin/hooks/account';
import { useMarginInstrumentDetails } from 'src/lib/margin/hooks/instruments';
import { useMarginPositionDetails, useUpdateMarginPosition } from 'src/lib/margin/hooks/positions';
import { MarginPositionDetailsStep } from 'src/lib/margin/types';
import { getMarginPartialCloseError } from 'src/lib/margin/utils';
import { useFormatInputText } from 'src/lib/utils/hooks/useFormatInputText';

import { MarginInstrument } from '../../../General';

type Props = {
  positionId?: string;
  onClose: () => void;
};

const MarginPositionDetailsPartialClosePositionStep: React.FC<Props> = ({ onClose, positionId }) => {
  const { currency } = useMarginAccount();
  const { position, positionMarket, positionMarketLotsPrecision, side, lots, currentPrice, profitLoss } =
    useMarginPositionDetails({
      positionId,
    });
  const { symbol } = useMarginInstrumentDetails({ instrument: positionMarket });
  const [loading, setLoading] = useState<boolean>(false);
  const { updatePosition, invalidateCache } = useUpdateMarginPosition();
  const { addSnackBar } = useSnackBars();
  const { t } = useTranslation('margin');
  const [closingAmount, setClosingAmount] = useState<string>('');
  const [percentage, setPercentage] = useState<number>(0);

  const remainingLots = useMemo(
    () =>
      Big(lots)
        .minus(closingAmount || 0)
        .toFixed(positionMarketLotsPrecision),
    [closingAmount, lots, positionMarketLotsPrecision],
  );

  const onChange = useCallback(
    (value: string) => {
      setClosingAmount(value);

      const percent = Big(value).div(lots).toString();
      setPercentage(Big(percent).times(100).toNumber());
    },
    [lots],
  );

  const { onChangeValue, formattedAmount } = useFormatInputText({
    asset: currency,
    value: closingAmount,
    blockAlphaNumericChars: true,
    priceScaleOverride: positionMarketLotsPrecision,
    onChange,
  });

  const isFullClose = useMemo(() => {
    if (!lots || !closingAmount) return false;
    return Big(remainingLots).eq(0);
  }, [closingAmount, lots, remainingLots]);

  const onUpdatePosition = useCallback(async () => {
    if (!position) return;

    try {
      setLoading(true);

      const positionUpdate: UpdateMarginPosition = {
        closed: DateTime.now().toMillis(),
        contracts: isFullClose ? undefined : Big(closingAmount).toString(),
      };

      await updatePosition(position.positionId, positionUpdate);

      addSnackBar({
        variant: 'success',
        content: position.sell
          ? t('protectionOrder.shortPositionClosed', { symbol })
          : t('protectionOrder.longPositionClosed', { symbol }),
        showIcon: true,
        duration: 5_000,
      });
    } catch (e) {
      addSnackBar({
        variant: 'error',
        content: t('protectionOrder.positionFailedClose', { symbol }),
        showIcon: true,
        duration: 5_000,
      });

      invalidateCache();
    } finally {
      setLoading(false);
      onClose();
    }
  }, [addSnackBar, closingAmount, invalidateCache, isFullClose, onClose, position, symbol, t, updatePosition]);

  const onValueChange = useCallback(
    (val: number[]) => {
      const [percent] = val;
      setPercentage(percent);

      const newAmount = Big(lots || '')
        .times(Big(percent).div(100))
        .round(positionMarketLotsPrecision, 0);

      setClosingAmount(newAmount.toString());
    },
    [lots, positionMarketLotsPrecision],
  );

  const error = useMemo(() => {
    if (!positionMarket || !closingAmount) return '';

    const errorCode = getMarginPartialCloseError(closingAmount, lots.toString(), positionMarket.minLots);

    switch (errorCode) {
      case undefined:
        return '';
      case MarginPartialCloseError.AboveRemainingLots:
        return t('position.errors.aboveRemainingLots');
      case MarginPartialCloseError.BelowMinimumLots:
        return t('position.errors.closeBelowMinimumLots', {
          minLots: formatCurrency(positionMarket?.minLots, undefined, { priceScale: positionMarketLotsPrecision }),
        });
      case MarginPartialCloseError.RemainingBelowMinimumLots:
        return t('position.errors.remainingBelowMinimumLots', {
          minLots: formatCurrency(positionMarket?.minLots, undefined, { priceScale: positionMarketLotsPrecision }),
        });
      default:
        errorCode satisfies never;
        return '';
    }
  }, [closingAmount, lots, positionMarket, positionMarketLotsPrecision, t]);

  return (
    <StepModal.Step
      key={MarginPositionDetailsStep.ClosePosition}
      onClose={onClose}
      hideActions
      title={t('position.partialClose.title')}
      position='dynamic'
    >
      <FlexLayout direction='column' spacing={24} className='h-full px-24 pb-24' justifyContent='space-between'>
        <SwyftxMarginStalePriceCloseBanner />
        <FlexLayout direction='column' spacing={12}>
          <ReviewItem title={t('position.instrument.title')}>
            <MarginInstrument market={positionMarket} size='medium' weight='bold' />
          </ReviewItem>
          {side !== undefined && (
            <ReviewItem title={t('position.side.title')}>
              <Body color='primary' weight='bold'>
                {t(`tradeSide.${side}`)}
              </Body>
            </ReviewItem>
          )}
          <ReviewItem title={t('position.sizeLots.title')}>
            <Numeric color='primary' weight='bold'>
              {formatCurrency(lots, undefined, { priceScale: positionMarketLotsPrecision })}
            </Numeric>
          </ReviewItem>

          {position?.openPrice && (
            <ReviewItem title={t('position.averageFillPrice.title')}>
              <Numeric color='primary' weight='bold'>
                {formatCurrency(position?.openPrice)}
              </Numeric>
            </ReviewItem>
          )}

          {currentPrice && (
            <ReviewItem title={t('position.currentPrice.title')}>
              <Numeric color='primary' weight='bold'>
                {formatCurrency(currentPrice)}
              </Numeric>
            </ReviewItem>
          )}
          <ReviewItem title={t('position.profitLoss.title')}>
            <Numeric color={profitLoss?.gte(0) ? 'success' : 'error'} weight='bold'>
              {formatCurrency(profitLoss, currency, { appendCode: true, hideCode: false })}
            </Numeric>
          </ReviewItem>

          <Separator className='my-16' />

          <FlexLayout direction='column' spacing={8}>
            <Input
              leading={
                <Body color='primary' className='whitespace-nowrap'>
                  {t('position.sizeLots.title')}
                </Body>
              }
              className='text-right'
              value={formattedAmount}
              onChange={onChangeValue}
              variant='monospace'
              placeholder={`Min ${formatCurrency(positionMarket?.minLots)}`}
              error={!!error}
            />
            {error && (
              <Body size='xsmall' color='error'>
                {error}
              </Body>
            )}
            <Slider marks={[0, 25, 50, 75, 100]} value={percentage} onValueChange={onValueChange} />
            <FlexLayout alignItems='center' justifyContent='space-between'>
              <Body size='xsmall' color='primary'>
                {t('position.sizeLots.minClose')}
              </Body>
              <Body size='xsmall' color='primary'>
                {t('position.sizeLots.maxClose')}
              </Body>
            </FlexLayout>
          </FlexLayout>

          <FlexLayout direction='column' spacing={12} className='mt-12'>
            {position?.volume && (
              <ReviewItem title={t('position.remainingPositionSizeLots.title')}>
                <Numeric color='primary'>
                  {formatCurrency(remainingLots, undefined, { priceScale: positionMarketLotsPrecision })}
                </Numeric>
              </ReviewItem>
            )}

            {profitLoss && (
              <ReviewItem
                title={t('position.estimatedProfitLoss.title')}
                tooltip={t('position.estimatedProfitLoss.tooltip')}
                underline='dashed'
              >
                <Numeric color={profitLoss?.gte(0) ? 'success' : 'error'} weight='bold'>
                  ≈
                  {formatCurrency(
                    Big(closingAmount || '0')
                      .div(lots)
                      .times(profitLoss)
                      .round(2, 0),
                    currency,
                    {
                      appendCode: true,
                      hideCode: false,
                    },
                  )}
                </Numeric>
              </ReviewItem>
            )}
          </FlexLayout>
        </FlexLayout>
        <Button
          color='destructive'
          size='lg'
          loading={loading}
          onClick={onUpdatePosition}
          disabled={Big(closingAmount || 0).eq(0) || !!error || !positionMarket?.close}
        >
          {isFullClose ? t('position.buttons.close') : t('position.buttons.partialClose')}
        </Button>
      </FlexLayout>
    </StepModal.Step>
  );
};

export { MarginPositionDetailsPartialClosePositionStep };
